import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Tag, Plus, RefreshCw, Trash2, Power, PowerOff, CheckCircle2, X, Loader2, Copy,
  ArrowLeft, ArrowRight,
} from "lucide-react";
import ConfirmDialog from "@/components/ConfirmDialog";

const DISCOUNTS_PAGE_SIZE = 15;

export default function AdminDiscounts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [page, setPage] = useState(0);
  const [confirmDel, setConfirmDel] = useState(null);
  const [form, setForm] = useState({
    code: "",
    percent_off: 20,
    amount_off: "",
    max_redemptions: "",
    expires_at: "",
    name: "",
    duration: "once",
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/discount-codes");
      setItems(data);
      setPage(0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const paged = useMemo(
    () => items.slice(page * DISCOUNTS_PAGE_SIZE, (page + 1) * DISCOUNTS_PAGE_SIZE),
    [items, page]
  );
  const totalPages = Math.max(1, Math.ceil(items.length / DISCOUNTS_PAGE_SIZE));

  const doDelete = async () => {
    if (!confirmDel) return;
    try {
      const { data } = await api.delete(`/admin/discount-codes/${confirmDel.id}`);
      toast.success(data.hard_delete ? "Codice eliminato" : "Codice disattivato (già usato, soft-delete)");
      setConfirmDel(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore eliminazione");
    }
  };

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        code: form.code.toUpperCase().trim(),
        duration: form.duration,
        name: form.name || form.code.toUpperCase().trim(),
      };
      if (form.percent_off) payload.percent_off = Number(form.percent_off);
      if (form.amount_off) payload.amount_off = Number(form.amount_off);
      if (form.max_redemptions) payload.max_redemptions = Number(form.max_redemptions);
      if (form.expires_at) payload.expires_at = new Date(form.expires_at).toISOString();
      await api.post("/admin/discount-codes", payload);
      toast.success(`Codice ${payload.code} creato`);
      setShowForm(false);
      setForm({ code: "", percent_off: 20, amount_off: "", max_redemptions: "", expires_at: "", name: "", duration: "once" });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore creazione");
    } finally {
      setCreating(false);
    }
  };

  const toggle = async (it) => {
    try {
      await api.patch(`/admin/discount-codes/${it.id}`, { active: !it.active });
      toast.success(it.active ? "Codice disattivato" : "Codice riattivato");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(`${code} copiato`);
    } catch (err) {
      // Clipboard API is blocked in some contexts (insecure origin,
      // permissions). Surface the failure so the admin isn't confused.
      console.warn("[copyCode] clipboard failed:", err?.message);
      toast.error("Copia non riuscita — copia manualmente");
    }
  };

  const kpi = useMemo(() => {
    const active = items.filter((i) => i.active);
    const redemptions = items.reduce((s, i) => s + Number(i.times_redeemed || 0), 0);
    return { total: items.length, active: active.length, redemptions };
  }, [items]);

  return (
    <div data-testid="admin-discounts">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">Codici sconto</h1>
          <p className="text-sm text-[#4A4A4A] mt-1">Promo codes Stripe applicabili al checkout — regali crediti scontati o gratuiti.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#E8E4D9] bg-white text-xs font-semibold hover:bg-[#F5F2EA]">
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Aggiorna
          </button>
          <button
            onClick={() => setShowForm(true)}
            data-testid="admin-discount-new-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#0A0A0A] text-white text-xs font-semibold hover:bg-[#1D4ED8]"
          >
            <Plus className="w-3.5 h-3.5" /> Nuovo codice
          </button>
        </div>
      </div>

      {/* KPI */}
      <div className="mt-6 grid grid-cols-3 gap-3">
        <Kpi label="Codici totali" value={kpi.total} />
        <Kpi label="Attivi" value={kpi.active} highlight="success" />
        <Kpi label="Redemption totali" value={kpi.redemptions} highlight="primary" />
      </div>

      {/* Table */}
      <div className="mt-6 rounded-2xl border border-[#E8E4D9] bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F5F2EA] border-b border-[#E8E4D9] text-[10px] uppercase tracking-[0.15em] text-[#737373] font-bold">
              <th className="text-left px-4 py-2.5">Codice</th>
              <th className="text-left px-4 py-2.5">Nome</th>
              <th className="text-left px-4 py-2.5 w-24">Sconto</th>
              <th className="text-left px-4 py-2.5 w-24">Durata</th>
              <th className="text-left px-4 py-2.5 w-32">Usi</th>
              <th className="text-left px-4 py-2.5 w-32">Scadenza</th>
              <th className="text-left px-4 py-2.5 w-20">Stato</th>
              <th className="text-right px-4 py-2.5 w-24">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="p-6 text-center text-[#737373]">Caricamento…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-[#737373] italic">Nessun codice ancora creato.</td></tr>
            )}
            {!loading && paged.map((it, i) => (
              <tr key={it.id} className={i > 0 ? "border-t border-[#E8E4D9]/70" : ""} data-testid={`admin-discount-row-${i}`}>
                <td className="px-4 py-2.5 font-mono font-bold text-[13px]">
                  <button onClick={() => copyCode(it.code)} className="inline-flex items-center gap-1.5 hover:text-[#1D4ED8]" title="Copia">
                    <Tag className="w-3 h-3" /> {it.code} <Copy className="w-3 h-3 opacity-50" />
                  </button>
                </td>
                <td className="px-4 py-2.5 text-xs text-[#4A4A4A]">{it.name}</td>
                <td className="px-4 py-2.5 font-bold text-[#1D4ED8]">
                  {it.percent_off ? `-${it.percent_off}%` : (it.amount_off ? `-€${it.amount_off.toFixed(2).replace(".", ",")}` : "—")}
                </td>
                <td className="px-4 py-2.5 text-[10px] uppercase tracking-wider text-[#737373]">{it.duration}</td>
                <td className="px-4 py-2.5 tabular-nums text-xs">
                  {it.times_redeemed || 0}
                  {it.max_redemptions ? <span className="text-[#737373]"> / {it.max_redemptions}</span> : <span className="text-[#737373]"> / ∞</span>}
                </td>
                <td className="px-4 py-2.5 text-xs font-mono">
                  {it.expires_at ? new Date(it.expires_at * 1000).toISOString().slice(0, 10) : "—"}
                </td>
                <td className="px-4 py-2.5">
                  {it.active ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded uppercase tracking-wider">
                      <CheckCircle2 className="w-3 h-3" /> Attivo
                    </span>
                  ) : (
                    <span className="text-[10px] font-bold text-[#737373] bg-[#F5F2EA] border border-[#E8E4D9] px-2 py-0.5 rounded uppercase tracking-wider">
                      Disattivo
                    </span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-right">
                  <button onClick={() => toggle(it)} data-testid={`admin-discount-toggle-${i}`}
                    className="inline-flex items-center px-2 py-1 rounded text-[10px] font-semibold text-[#0A0A0A] hover:bg-[#F5F2EA]"
                    title={it.active ? "Disattiva" : "Attiva"}
                  >
                    {it.active ? <PowerOff className="w-3.5 h-3.5" /> : <Power className="w-3.5 h-3.5" />}
                  </button>
                  <button onClick={() => setConfirmDel(it)} data-testid={`admin-discount-delete-${i}`}
                    className="inline-flex items-center px-2 py-1 rounded text-[10px] font-semibold text-red-700 hover:bg-red-50 ml-1"
                    title="Elimina codice"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between text-xs" data-testid="discounts-pager">
          <div className="text-[#737373]">
            {page * DISCOUNTS_PAGE_SIZE + 1}–{Math.min((page + 1) * DISCOUNTS_PAGE_SIZE, items.length)} su {items.length}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              data-testid="discounts-pager-prev"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#E8E4D9] bg-white disabled:opacity-40 hover:bg-[#F5F2EA] font-semibold"
            >
              <ArrowLeft className="w-3 h-3" /> Precedente
            </button>
            <span className="tabular-nums font-semibold">Pag. {page + 1} / {totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page + 1 >= totalPages}
              data-testid="discounts-pager-next"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#E8E4D9] bg-white disabled:opacity-40 hover:bg-[#F5F2EA] font-semibold"
            >
              Successiva <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmDel}
        onOpenChange={(v) => !v && setConfirmDel(null)}
        onConfirm={doDelete}
        icon={Trash2}
        tone="danger"
        title={`Eliminare codice ${confirmDel?.code}?`}
        description={
          confirmDel?.times_redeemed
            ? `Questo codice è già stato usato ${confirmDel.times_redeemed} volta/e. Non può essere cancellato completamente da Stripe: verrà disattivato (soft-delete). Nessun ordine passato viene toccato.`
            : "Il codice sconto e il coupon Stripe collegato verranno rimossi definitivamente. Nessun ordine passato viene toccato."
        }
        confirmLabel="Sì, elimina"
      />

      {/* Create form modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => setShowForm(false)}>
          <form
            onSubmit={create}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden"
            data-testid="admin-discount-form"
          >
            <div className="border-b border-[#E8E4D9] px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="font-display font-bold text-xl">Nuovo codice sconto</h3>
                <p className="text-xs text-[#737373] mt-0.5">Verrà creato su Stripe e applicabile al checkout.</p>
              </div>
              <button type="button" onClick={() => setShowForm(false)} className="p-1.5 hover:bg-[#F5F2EA] rounded-md">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6 grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Codice (uppercase)</Label>
                <input required value={form.code} onChange={(e) => setForm({...form, code: e.target.value.toUpperCase()})}
                  placeholder="LAUNCH20" data-testid="discount-code-input"
                  className="w-full px-3 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm font-mono uppercase" />
              </div>
              <div>
                <Label>Sconto % (1-100)</Label>
                <input type="number" min="1" max="100" value={form.percent_off}
                  onChange={(e) => setForm({...form, percent_off: e.target.value, amount_off: ""})}
                  className="w-full px-3 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm tabular-nums" />
              </div>
              <div>
                <Label>OPPURE sconto € fisso</Label>
                <input type="number" step="0.01" min="0" value={form.amount_off}
                  placeholder="es. 5,00"
                  onChange={(e) => setForm({...form, amount_off: e.target.value, percent_off: ""})}
                  className="w-full px-3 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm tabular-nums" />
              </div>
              <div>
                <Label>Usi massimi (opzionale)</Label>
                <input type="number" min="1" value={form.max_redemptions}
                  onChange={(e) => setForm({...form, max_redemptions: e.target.value})}
                  placeholder="∞" className="w-full px-3 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm tabular-nums" />
              </div>
              <div>
                <Label>Scadenza (opzionale)</Label>
                <input type="datetime-local" value={form.expires_at}
                  onChange={(e) => setForm({...form, expires_at: e.target.value})}
                  className="w-full px-3 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm" />
              </div>
              <div className="col-span-2">
                <Label>Nome interno (opzionale)</Label>
                <input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                  placeholder="Campagna lancio Q1 2026"
                  className="w-full px-3 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm" />
              </div>
              <div className="col-span-2 rounded-lg bg-blue-50 border border-blue-200 p-3 text-xs text-[#1D4ED8]">
                💡 <strong>100%</strong> = codice omaggio (crediti gratis). Il codice verrà mostrato sul checkout Stripe quando l&apos;utente clicca <em>&laquo;Ho un codice&raquo;</em>.
              </div>
            </div>
            <div className="border-t border-[#E8E4D9] px-6 py-4 flex items-center justify-end gap-2">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm">Annulla</button>
              <button type="submit" disabled={creating || !form.code}
                data-testid="admin-discount-save"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#0A0A0A] text-white text-sm font-semibold disabled:opacity-60">
                {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Crea codice
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Label({ children }) {
  return <label className="block text-[10px] uppercase tracking-wider text-[#737373] font-bold mb-1.5">{children}</label>;
}

function Kpi({ label, value, highlight }) {
  const c = highlight === "success" ? "text-emerald-700 bg-emerald-50 border-emerald-200"
    : highlight === "primary" ? "text-[#1D4ED8] bg-blue-50 border-blue-200"
    : "text-[#0A0A0A] bg-white border-[#E8E4D9]";
  return (
    <div className={`rounded-2xl border ${c} p-4`}>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-70">{label}</div>
      <div className="font-display font-extrabold text-2xl mt-1 tabular-nums">{value}</div>
    </div>
  );
}
